import type { Gender, Patient } from '@/lib/clinic/types'

import { getAgeFromDob } from '@/lib/clinic/datetime'

const GENDER_LABELS: Record<Gender, string> = {
    female: 'Female',
    male: 'Male',
    other: 'Other',
    '': 'Not specified'
}

const AVATAR_TONES = [
    'bg-teal-100 text-teal-800',
    'bg-sky-100 text-sky-800',
    'bg-cyan-100 text-cyan-800',
    'bg-emerald-100 text-emerald-800',
    'bg-indigo-100 text-indigo-800',
    'bg-blue-100 text-blue-800'
]

export const phoneDigits = (phone: string): string => phone.replace(/\D/g, '')

export const formatPhone = (phone: string): string => {
    const trimmed = phone.trim()
    const digits = phoneDigits(trimmed)

    if (digits.length === 10 && !trimmed.startsWith('+')) return `${digits.slice(0, 5)} ${digits.slice(5)}`
    if (digits.length === 12 && digits.startsWith('91')) return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`

    return trimmed
}

export const telHref = (phone: string): string => {
    const trimmed = phone.trim()

    return `tel:${trimmed.startsWith('+') ? '+' : ''}${phoneDigits(trimmed)}`
}

export const genderLabel = (gender: Gender): string => GENDER_LABELS[gender] ?? GENDER_LABELS['']

export const getPatientAge = (patient: Pick<Patient, 'age' | 'dateOfBirth'>, today: string): number | null => {
    if (patient.dateOfBirth && today) return getAgeFromDob(patient.dateOfBirth, today)

    return patient.age
}

export const formatAge = (age: number | null): string => {
    if (age === null || Number.isNaN(age)) return '—'
    if (age === 0) return 'Under 1 yr'

    return age === 1 ? '1 yr' : `${age} yrs`
}

export const ageGenderLabel = (patient: Pick<Patient, 'age' | 'dateOfBirth' | 'gender'>, today: string): string => {
    const parts: string[] = []
    const age = getPatientAge(patient, today)

    if (age !== null) parts.push(formatAge(age))
    if (patient.gender) parts.push(genderLabel(patient.gender))

    return parts.length ? parts.join(' · ') : 'Age and gender not recorded'
}

export const initials = (name: string): string => {
    const letters = name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('')

    return letters || '?'
}

export const avatarTone = (seed: string): string => {
    let hash = 0

    for (const char of seed) hash = (hash * 31 + char.charCodeAt(0)) >>> 0

    return AVATAR_TONES[hash % AVATAR_TONES.length]
}

export const pluralize = (count: number, singular: string, plural = `${singular}s`): string => {
    return `${count} ${count === 1 ? singular : plural}`
}

export const joinList = (items: string[]): string => items.map(item => item.trim()).filter(Boolean).join(', ')

export const describeComplaint = (complaints: string[], note: string): string => {
    const list = joinList(complaints)

    if (list && note.trim()) return `${list}. ${note.trim()}`

    return list || note.trim()
}

export const firstLine = (value: string): string => value.split('\n').map(line => line.trim()).find(Boolean) ?? ''

export const slugify = (value: string): string => {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
}

export const appendToText = (current: string, addition: string): string => {
    const trimmed = current.trim()

    if (!trimmed) return addition
    if (trimmed.toLowerCase().includes(addition.toLowerCase())) return current

    return `${trimmed.replace(/[,.]$/, '')}, ${addition}`
}
